import axios from 'axios';
import {
  ContentDocument,
  ContentDocumentDetail,
  DocumentMetadata,
  ProcessingStatusResponse,
  DocumentFilters,
  PaginatedDocuments,
  ExamOEMapping,
  QuestionGenerationRequest,
  GenerationProgress,
} from '../types/content.types';
import { API_BASE_URL } from '../config/api.config';

const API_URL = API_BASE_URL;

// Re-export types for backward compatibility
export type { ContentDocument, DocumentMetadata };

// Keep PDFMetadata for backward compatibility
export type PDFMetadata = DocumentMetadata;

export interface UploadResponse {
  documentId: string;
  message: string;
}

class ContentService {
  /**
   * Upload document file (any type supported) for RAG processing
   */
  async uploadDocument(file: File, metadata: DocumentMetadata, accessToken: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    const response = await axios.post(`${API_URL}/content/upload`, formData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data.data;
  }

  /**
   * Upload PDF file (backward compatibility)
   */
  async uploadPDF(file: File, metadata: PDFMetadata, accessToken: string): Promise<UploadResponse> {
    return this.uploadDocument(file, metadata, accessToken);
  }

  /**
   * Get document processing status
   */
  async getStatus(documentId: string, accessToken: string): Promise<ProcessingStatusResponse> {
    const response = await axios.get(`${API_URL}/content/${documentId}/status`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    return response.data.data;
  }

  /**
   * Transform backend content document to frontend LearningResource
   */
  private transformDocument(doc: any): any {
    return {
      resource_id: doc._id,
      exam_id: doc.exam,
      course_id: doc.course,
      resource_type: doc.category || 'official_doc',
      title: doc.title,
      description: doc.description,
      author: doc.publisher,
      source_authority: 'official',
      file_path: doc.s3Url,
      file_type: doc.mimeType,
      url: doc.s3Url,
      language: doc.language,
      version: doc.version,
      is_active: true, // All documents are active by default
      publication_date: doc.publishedDate,
      processing_status: doc.status,
      processing_progress: doc.processingProgress,
      chunk_count: doc.numChunks,
      file_size_mb: doc.fileSize ? (doc.fileSize / (1024 * 1024)).toFixed(2) : 0,
      pages: doc.numPages,
      error_message: doc.processingError,
      created_at: doc.uploadedAt || doc.createdAt,
      updated_at: doc.lastUpdated || doc.updatedAt,
      ...doc, // Include all other fields
    };
  }

  /**
   * Get all documents with filters
   */
  async getDocuments(
    filters: DocumentFilters,
    accessToken: string
  ): Promise<PaginatedDocuments> {
    const params = new URLSearchParams();
    if (filters.exam) params.append('exam', filters.exam);
    if (filters.course) params.append('course', filters.course);
    if (filters.category) params.append('category', filters.category);
    if (filters.documentType) params.append('documentType', filters.documentType);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort) params.append('sort', filters.sort);

    const response = await axios.get(`${API_URL}/content?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const data = response.data.data;

    // Transform documents to match frontend expectations
    return {
      ...data,
      documents: data.documents.map((doc: any) => this.transformDocument(doc))
    };
  }

  /**
   * Get recent uploads for dashboard widget
   */
  async getRecentUploads(limit: number = 10, accessToken: string): Promise<ContentDocument[]> {
    const response = await this.getDocuments(
      { limit, sort: '-uploadedAt' },
      accessToken
    );
    return response.documents;
  }

  /**
   * Delete document
   */
  async deleteDocument(documentId: string, accessToken: string): Promise<void> {
    await axios.delete(`${API_URL}/content/${documentId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  }

  /**
   * Delete all documents
   */
  async deleteAllDocuments(accessToken: string): Promise<{ deleted: number }> {
    const response = await axios.delete(`${API_URL}/content/all`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data;
  }

  /**
   * Download document file using S3 URL
   */
  async downloadDocument(s3Url: string, filename: string): Promise<void> {
    try {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = s3Url;
      link.target = '_blank';
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  /**
   * Retry failed document processing
   */
  async retryProcessing(documentId: string, accessToken: string): Promise<void> {
    await axios.post(`${API_URL}/content/${documentId}/retry`, {}, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  }

  /**
   * Get document details with chapters
   */
  async getDocumentDetails(documentId: string, accessToken: string): Promise<ContentDocumentDetail> {
    const response = await axios.get(`${API_URL}/content/${documentId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }

  /**
   * Get exam OE mappings
   */
  async getExamMappings(exam: string, accessToken: string): Promise<ExamOEMapping[]> {
    const response = await axios.get(`${API_URL}/exams/${exam}/mappings`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }

  /**
   * Create or update OE mapping
   */
  async saveExamMapping(mapping: Partial<ExamOEMapping>, accessToken: string): Promise<ExamOEMapping> {
    const response = await axios.post(`${API_URL}/exams/mappings`, mapping, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }

  /**
   * Approve AI-suggested mapping
   */
  async approveMapping(mappingId: string, accessToken: string): Promise<void> {
    await axios.post(`${API_URL}/exams/mappings/${mappingId}/approve`, {}, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  }

  /**
   * Delete OE mapping
   */
  async deleteMapping(mappingId: string, accessToken: string): Promise<void> {
    await axios.delete(`${API_URL}/exams/mappings/${mappingId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
  }

  /**
   * Start question generation
   */
  async startGeneration(request: QuestionGenerationRequest, accessToken: string): Promise<{
    questions: any[];
    totalGenerated: number;
    totalSaved: number;
    metadata: any;
  }> {
    // Don't save questions yet - user will review in preview first, then save manually
    const requestWithSave = {
      ...request,
      saveToDatabase: false
    };

    const response = await axios.post(`${API_URL}/questions/generate`, requestWithSave, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }

  /**
   * 🎨 Generate questions with Custom Prompt (AI-Guided Mode)
   * Uses dedicated custom prompt endpoint with MCP semantic search
   */
  async generateWithCustomPrompt(
    prompt: string,
    exam: string,
    totalQuestions: number,
    options: {
      questionType?: string;
      difficulty?: string;
      documentIds?: string[];
      course?: string;
      courseName?: string;
    },
    accessToken: string
  ): Promise<{
    questions: any[];
    totalGenerated: number;
    metadata: any;
  }> {
    const response = await axios.post(
      `${API_URL}/questions/orchestrate`,
      {
        prompt,
        exam,
        totalQuestions,
        questionType: options.questionType || 'MCQ',
        difficulty: options.difficulty || 'medium',
        documentIds: options.documentIds,
        course: options.course,
        courseName: options.courseName
      },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data.data;
  }

  /**
   * Get generation progress
   */
  async getGenerationProgress(requestId: string, accessToken: string): Promise<GenerationProgress> {
    const response = await axios.get(`${API_URL}/questions/generate/${requestId}/progress`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }

  /**
   * Get AI-suggested OE codes from documents
   */
  async getAISuggestedOEs(exam: string, accessToken: string): Promise<ExamOEMapping[]> {
    const response = await axios.get(`${API_URL}/exams/${exam}/suggested-mappings`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }


  /**
   * Get extracted topics for a document
   */
  async getDocumentTopics(documentId: string, accessToken: string): Promise<DocumentTopicsResponse> {
    const response = await axios.get(`${API_URL}/content/${documentId}/topics`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data.data;
  }

  /**
   * Get all topics from all completed documents
   */
  async getAllTopics(accessToken: string): Promise<TopicWithDocument[]> {
    const response = await axios.get(
      `${API_URL}/content/topics/all`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data.data;
  }

  /**
   * Delete all extracted topics
   */
  async deleteAllTopics(accessToken: string): Promise<{ deleted: number }> {
    const response = await axios.delete(
      `${API_URL}/content/topics/all`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data.data;
  }

  /**
   * Delete a single topic
   */
  async deleteTopic(documentId: string, topicTitle: string, accessToken: string): Promise<{ deleted: number }> {
    const response = await axios.delete(
      `${API_URL}/content/${documentId}/topics/${encodeURIComponent(topicTitle)}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data.data;
  }

  /**
   * Get topics filtered by exam
   */
  async getTopicsByExam(examId: string): Promise<TopicWithDocument[]> {
    const accessToken = localStorage.getItem('accessToken');
    const response = await axios.get(
      `${API_URL}/content/topics/all`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        params: {
          exam: examId
        }
      }
    );
    return response.data.data || response.data.topics || response.data || [];
  }

  /**
   * Extract topics for all existing documents
   */
  async extractAllTopics(accessToken: string): Promise<{
    total: number;
    queued: number;
    alreadyProcessed: number;
    skipped: number;
  }> {
    const response = await axios.post(
      `${API_URL}/content/extract-all-topics`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data.data;
  }
}

// Topic types
export interface Topic {
  title: string;
  description: string;
  keywords: string[];
  importance: 'high' | 'medium' | 'low';
  relatedOECodes: string[];
  pageReferences: number[];
  estimatedQuestions: number;
}

export interface DocumentTopicsResponse {
  documentId: string;
  filename: string;
  exam: string;
  topics: Topic[];
  extractedAt: Date;
  totalTopics: number;
  highPriorityTopics: number;
  mediumPriorityTopics: number;
  lowPriorityTopics: number;
  status: string;
}

export interface TopicWithDocument extends Topic {
  documentId: string;
  documentTitle: string;
  documentExam: string;
}

export const contentService = new ContentService();
